import { useInput, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ColegaPickerInput } from "../colegas/ColegaPickerInput";
import type { CorretajeSaleInput } from "../corretaje/corretajeSale";
import {
  CORRETAJE_COLEGA_FOUND_BY,
  CORRETAJE_PROPERTY_OWNERS,
  type CorretajeColegaFoundBy,
  type CorretajeCommissionScheme,
  type CorretajePropertyOwner,
  type CorretajeSaleAdministration,
  type OperationSummary,
  type PropertyOption,
  type Sale,
} from "../types";

export const EMPTY = "—";
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base disabled:opacity-60";

/** Raw values of the form fields (strings, as the inputs hold them). */
export type FormValues = {
  shared: "si" | "no";
  colega_id: CorretajeSaleAdministration["colega_id"];
  found_by: CorretajeColegaFoundBy | "" | null;
  property_owner: CorretajePropertyOwner;
  commission: string | number | null;
};

/** Everything the sale card shows, loaded once by `CorretajeSaleAdmin`. */
export type Context = {
  operation: OperationSummary;
  admin: CorretajeSaleAdministration;
  option: PropertyOption;
  schemes: CorretajeCommissionScheme[];
  opcionadoraText: string;
  vendedoraText: string;
};

export const toFormValues = (
  admin: CorretajeSaleAdministration,
): FormValues => ({
  shared: admin.shared_with_colega ? "si" : "no",
  colega_id: admin.colega_id ?? null,
  found_by: admin.colega_found_by ?? "",
  property_owner: admin.property_owner,
  commission: admin.commission_total ?? "",
});

/**
 * Form values -> the facts the server receives. Not shared means no colega, no
 * "who found her" and our own inventory (the colega's property needs a colega).
 */
export const toSaleInput = (values: FormValues): CorretajeSaleInput => {
  const shared = values.shared === "si";
  const commission =
    values.commission === "" || values.commission == null
      ? null
      : Number(values.commission);
  return {
    shared_with_colega: shared,
    colega_id: shared ? (values.colega_id ?? null) : null,
    colega_found_by: shared ? values.found_by || null : null,
    property_owner: shared ? values.property_owner : "almikia",
    commission_total: Number.isNaN(commission) ? null : commission,
  };
};

export const advisorLabel = (sale: Sale | undefined): string =>
  sale
    ? [
        `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim(),
        sale.advisor_code,
      ]
        .filter(Boolean)
        .join(" · ")
    : EMPTY;

const Field = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

const ChoiceSelect = ({
  source,
  label,
  choices,
  allowEmpty = false,
  disabledChoices = [],
  onValueChange,
}: {
  source: string;
  label: string;
  choices: Array<{ id: string; name: string }>;
  allowEmpty?: boolean;
  disabledChoices?: string[];
  onValueChange?: (value: string) => void;
}) => {
  const translate = useTranslate();
  const { field } = useInput({ source });
  return (
    <Field id={`sale-${source}`} label={label}>
      <select
        id={`sale-${source}`}
        className={SELECT_CLASS}
        name={field.name}
        value={field.value ?? ""}
        onChange={(event) => {
          field.onChange(event);
          onValueChange?.(event.target.value);
        }}
        onBlur={field.onBlur}
      >
        {allowEmpty ? (
          <option value="">{translate("crm.corretaje.sale.choose")}</option>
        ) : null}
        {choices.map((choice) => (
          <option
            key={choice.id}
            value={choice.id}
            disabled={disabledChoices.includes(choice.id)}
          >
            {choice.name}
          </option>
        ))}
      </select>
    </Field>
  );
};

const CommissionInput = ({ label }: { label: string }) => {
  const { field } = useInput({ source: "commission" });
  return (
    <Field id="sale-commission" label={label}>
      <Input
        id="sale-commission"
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        className="h-11 tabular-nums"
        name={field.name}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </Field>
  );
};

const OpcionadoraNote = ({
  context,
  owner,
}: {
  context: Context;
  owner: CorretajePropertyOwner;
}) => {
  const translate = useTranslate();
  return (
    <p data-testid="sale-opcionadora" className="text-base">
      <span className="text-sm text-muted-foreground">
        {translate("crm.corretaje.sale.opcionadora")}:{" "}
      </span>
      {owner === "colega"
        ? translate("crm.corretaje.sale.opcionadora_vendedora", {
            name: context.vendedoraText,
          })
        : context.opcionadoraText}
    </p>
  );
};

/**
 * The editable fields of the sale form. Switching "shared" back to "no" resets
 * the property owner to our own inventory so the select never shows a value the
 * server will not save.
 */
export const FormFields = ({ context }: { context: Context }) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const values = useWatch() as FormValues;
  const shared = values.shared === "si";
  const owner = shared ? values.property_owner : "almikia";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <ChoiceSelect
        source="shared"
        label={translate("crm.corretaje.sale.shared")}
        choices={[
          { id: "no", name: translate("crm.corretaje.sale.no") },
          { id: "si", name: translate("crm.corretaje.sale.yes") },
        ]}
        onValueChange={(value) => {
          if (value === "no") {
            setValue("property_owner", "almikia", { shouldDirty: true });
          }
        }}
      />
      {shared ? (
        <>
          <ColegaPickerInput source="colega_id" />
          <ChoiceSelect
            source="found_by"
            allowEmpty
            label={translate("crm.corretaje.sale.found_by")}
            choices={CORRETAJE_COLEGA_FOUND_BY.map((id) => ({
              id,
              name: translate(`crm.corretaje.sale.found_by_options.${id}`),
            }))}
          />
        </>
      ) : null}
      <ChoiceSelect
        source="property_owner"
        label={translate("crm.corretaje.sale.property_owner")}
        choices={CORRETAJE_PROPERTY_OWNERS.map((id) => ({
          id,
          name: translate(`crm.corretaje.sale.property_owner_options.${id}`),
        }))}
        disabledChoices={shared ? [] : ["colega"]}
      />
      <OpcionadoraNote context={context} owner={owner} />
      <p data-testid="sale-vendedora" className="text-base">
        <span className="text-sm text-muted-foreground">
          {translate("crm.corretaje.sale.vendedora")}:{" "}
        </span>
        {context.vendedoraText}
      </p>
      <CommissionInput
        label={translate("crm.corretaje.sale.commission_total")}
      />
    </div>
  );
};
